/**
 * 블로그 목록 페이지.
 *
 * 영구지침: docs/develop/seo/blog-cms-spec.md §7.1
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { BookOpen, Calendar, User } from 'lucide-react'
import SubpageHero from '@/components/common/SubpageHero'
import PageContainer from '@/components/layout/PageContainer'
import Spinner from '@/components/ui/Spinner'
import SEO from '@/components/common/SEO'
import { BreadcrumbJsonLd } from '@/components/common/JsonLd'
import { useBlogPosts } from '@/features/blog/api'
import {
  BLOG_CATEGORIES,
  BLOG_CATEGORY_LABELS,
  type BlogCategory,
} from '@/lib/blog/types'

export default function BlogListPage() {
  const [filter, setFilter] = useState<BlogCategory | 'all'>('all')
  const { data: page, isLoading } = useBlogPosts({ size: 100 })
  const allPosts = page?.content ?? []
  const filtered =
    filter === 'all' ? allPosts : allPosts.filter((p) => p.category === filter)

  return (
    <>
      <SEO
        title="요양 정보 블로그 - 요양시설·비용·등급 가이드"
        description="요양원 비용, 장기요양등급 신청, 시설 선택 등 보호자가 알아야 할 모든 정보를 한 곳에서 확인하세요."
        url="/blog"
      />
      <BreadcrumbJsonLd
        items={[
          { name: '홈', url: '/' },
          { name: '블로그', url: '/blog' },
        ]}
      />
      <SubpageHero
        title="케어밸런스 블로그"
        description="보호자가 알아야 할 요양 정보를 한 곳에서"
        icon={<BookOpen size={20} className="text-white" />}
      />
      <PageContainer noMaxWidth>
        <div className="max-w-6xl mx-auto">
        {/* 카테고리 필터 */}
        <div className="flex flex-wrap gap-2 my-6">
          <button
            onClick={() => setFilter('all')}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
              filter === 'all'
                ? 'bg-cb-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            전체 ({allPosts.length})
          </button>
          {BLOG_CATEGORIES.map((cat) => {
            const count = allPosts.filter((p) => p.category === cat).length
            if (count === 0) return null
            return (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                  filter === cat
                    ? 'bg-cb-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {BLOG_CATEGORY_LABELS[cat]} ({count})
              </button>
            )
          })}
        </div>

        {/* 카드 그리드 */}
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            아직 발행된 글이 없습니다
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
            {filtered.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group rounded-2xl border border-gray-200 bg-white overflow-hidden hover:shadow-lg transition-shadow"
              >
                {post.thumbnailUrl ? (
                  <div className="aspect-[16/9] overflow-hidden bg-gray-100">
                    <img
                      src={post.thumbnailUrl}
                      alt={post.title}
                      loading="lazy"
                      className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform"
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/9] bg-gradient-to-br from-cb-primary-light to-white flex items-center justify-center">
                    <BookOpen size={48} className="text-cb-primary/30" />
                  </div>
                )}
                <div className="p-5">
                  <span className="inline-block rounded-full bg-cb-primary-light px-3 py-0.5 text-xs font-semibold text-cb-primary mb-3">
                    {BLOG_CATEGORY_LABELS[post.category]}
                  </span>
                  <h3 className="text-base font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-cb-primary transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {post.summary}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <User size={12} />
                      {post.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {post.publishedAt?.slice(0, 10) ?? ''}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
        </div>
      </PageContainer>
    </>
  )
}
